import React from 'react';
import PolicyPageLayout from '../components/PolicyPageLayout';

const PaymentMethods = () => {
  const sections = [
    {
      title: 'Accepted Payment Methods',
      content: (
        <>
          <p>FUWJALS accepts various payment methods to accommodate authors worldwide. Choose the method that works best for you based on your location and preferences.</p>
          <div className="alert alert-info mt-3">
            <i className="fas fa-info-circle me-2"></i>
            All payments are processed securely through encrypted channels. Your financial information is protected at all times.
          </div>
        </>
      )
    },
    {
      title: 'International Payment Options',
      content: (
        <>
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card border-primary h-100">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0"><i className="fab fa-paypal me-2"></i>PayPal</h5>
                </div>
                <div className="card-body">
                  <p><strong>Processing Time:</strong> Instant</p>
                  <p><strong>Currencies:</strong> USD, EUR, GBP, and others</p>
                  <p><strong>Fees:</strong> Standard PayPal fees apply</p>
                  <p><strong>Requirements:</strong> PayPal account</p>
                  <div className="alert alert-success mt-auto">
                    <small><i className="fas fa-star"></i> Most convenient for international authors</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card border-success h-100">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0"><i className="fas fa-credit-card me-2"></i>Credit/Debit Cards</h5>
                </div>
                <div className="card-body">
                  <p><strong>Processing Time:</strong> Instant</p>
                  <p><strong>Cards Accepted:</strong> Visa, MasterCard, AMEX</p>
                  <p><strong>Security:</strong> SSL encrypted</p>
                  <p><strong>Currencies:</strong> USD, EUR, GBP</p>
                  <div className="alert alert-info mt-auto">
                    <small><i className="fas fa-lock"></i> Secure online payment gateway</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card border-warning h-100">
                <div className="card-header bg-warning text-dark">
                  <h5 className="mb-0"><i className="fas fa-university me-2"></i>Bank Wire Transfer</h5>
                </div>
                <div className="card-body">
                  <p><strong>Processing Time:</strong> 3-5 business days</p>
                  <p><strong>Currencies:</strong> USD, EUR, GBP</p>
                  <p><strong>Fees:</strong> Bank transfer fees apply</p>
                  <p><strong>Requirements:</strong> Bank account</p>
                  <div className="alert alert-warning mt-auto">
                    <small><i className="fas fa-building"></i> Preferred for large institutions</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card border-danger h-100">
                <div className="card-header bg-danger text-white">
                  <h5 className="mb-0"><i className="fas fa-money-bill-wave me-2"></i>Money Transfer</h5>
                </div>
                <div className="card-body">
                  <p><strong>Services:</strong> Western Union, MoneyGram</p>
                  <p><strong>Processing Time:</strong> Same day</p>
                  <p><strong>Requirements:</strong> Photo ID</p>
                  <p><strong>Availability:</strong> Worldwide</p>
                  <div className="alert alert-light mt-auto">
                    <small><i className="fas fa-globe"></i> For authors without bank accounts</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      title: 'Nigerian Payment Methods',
      content: (
        <>
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card border-info h-100">
                <div className="card-header bg-info text-white">
                  <h5 className="mb-0"><i className="fas fa-mobile-alt me-2"></i>Mobile Money</h5>
                </div>
                <div className="card-body">
                  <p><strong>Platforms:</strong> MTN Mobile Money, Airtel Money</p>
                  <p><strong>Processing Time:</strong> Instant</p>
                  <p><strong>Currency:</strong> Nigerian Naira (NGN)</p>
                  <p><strong>Convenience:</strong> High</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card border-secondary h-100">
                <div className="card-header bg-secondary text-white">
                  <h5 className="mb-0"><i className="fas fa-building me-2"></i>Bank Transfer (NGN)</h5>
                </div>
                <div className="card-body">
                  <p><strong>Banks:</strong> All major Nigerian banks</p>
                  <p><strong>Processing Time:</strong> Same day</p>
                  <p><strong>Method:</strong> Online or branch</p>
                  <p><strong>Fees:</strong> Minimal</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card border-primary h-100">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0"><i className="fas fa-laptop me-2"></i>Online Banking</h5>
                </div>
                <div className="card-body">
                  <p><strong>Platforms:</strong> All bank web portals</p>
                  <p><strong>Security:</strong> Bank-grade encryption</p>
                  <p><strong>Convenience:</strong> 24/7 availability</p>
                  <p><strong>Requirements:</strong> Online banking account</p>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card border-success h-100">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0"><i className="fas fa-money-check-alt me-2"></i>Bank Draft</h5>
                </div>
                <div className="card-body">
                  <p><strong>Payable to:</strong> Federal University Wukari</p>
                  <p><strong>Processing Time:</strong> 2-3 days</p>
                  <p><strong>Collection:</strong> Physical delivery required</p>
                  <p><strong>Security:</strong> High</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      title: 'Payment Process',
      content: 'Follow these simple steps to complete your payment:',
      list: [
        'Manuscript Acceptance: Receive acceptance notification with manuscript ID',
        'Invoice Generation: Receive payment invoice within 24 hours of acceptance',
        'Payment Method Selection: Choose your preferred payment method from available options',
        'Payment Completion: Complete payment using selected method and save confirmation',
        'Confirmation Submission: Submit payment confirmation via email or portal',
        'Verification: Our team verifies payment within 1-3 business days',
        'Publication: Article proceeds to final production and publication'
      ],
      highlight: (
        <>
          <i className="fas fa-exclamation-triangle me-2"></i>
          Publication will commence only after payment confirmation. Please submit payment proof immediately after completing payment.
        </>
      )
    },
    {
      title: 'Payment Security',
      content: (
        <>
          <div className="row">
            <div className="col-md-6">
              <h5 className="text-primary">Security Measures</h5>
              <ul className="policy-section-list">
                <li>SSL encryption for all transactions</li>
                <li>PCI DSS compliant payment processors</li>
                <li>No storage of payment card information</li>
                <li>Secure payment confirmation system</li>
                <li>Fraud detection and prevention</li>
                <li>Regular security audits</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h5 className="text-primary">Privacy Protection</h5>
              <ul className="policy-section-list">
                <li>Payment information not shared with third parties</li>
                <li>Confidential transaction processing</li>
                <li>GDPR compliant data handling</li>
                <li>Secure receipt generation</li>
                <li>Protected financial records</li>
                <li>Encrypted communication channels</li>
              </ul>
            </div>
          </div>
        </>
      )
    },
    {
      title: 'Currency and Processing Fees',
      content: (
        <>
          <div className="table-responsive">
            <table className="table table-hover table-bordered">
              <thead className="table-primary">
                <tr>
                  <th>Payment Method</th>
                  <th>Processing Fee</th>
                  <th>Exchange Rate</th>
                  <th>Total Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><i className="fab fa-paypal me-2"></i>PayPal</td>
                  <td>2.9% + $0.30</td>
                  <td>PayPal rate</td>
                  <td className="text-success">Instant</td>
                </tr>
                <tr>
                  <td><i className="fas fa-credit-card me-2"></i>Credit/Debit Card</td>
                  <td>2.5%</td>
                  <td>Market rate</td>
                  <td className="text-success">Instant</td>
                </tr>
                <tr>
                  <td><i className="fas fa-university me-2"></i>Bank Transfer</td>
                  <td>$15-25</td>
                  <td>Bank rate</td>
                  <td className="text-warning">3-5 days</td>
                </tr>
                <tr>
                  <td><i className="fas fa-mobile-alt me-2"></i>Mobile Money (NGN)</td>
                  <td>1-2%</td>
                  <td>CBN rate</td>
                  <td className="text-success">Instant</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-muted mt-2">
            <small>* Processing fees are charged by payment providers and are additional to publication fees</small>
          </p>
        </>
      )
    },
    {
      title: 'Bank Account Details',
      content: (
        <>
          <div className="row">
            <div className="col-md-6">
              <div className="bg-light p-3 rounded">
                <h5 className="text-primary">USD Account</h5>
                <p className="mb-2">
                  <strong>Bank:</strong> First Bank of Nigeria<br />
                  <strong>Account Name:</strong> Federal University Wukari<br />
                  <strong>Account Number:</strong> XXXXXXXXXX<br />
                  <strong>SWIFT Code:</strong> FIRSTNGN<br />
                  <strong>Branch:</strong> Wukari Branch
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="bg-light p-3 rounded">
                <h5 className="text-primary">NGN Account</h5>
                <p className="mb-2">
                  <strong>Bank:</strong> First Bank of Nigeria<br />
                  <strong>Account Name:</strong> Federal University Wukari<br />
                  <strong>Account Number:</strong> YYYYYYYYYY<br />
                  <strong>Sort Code:</strong> 011152001<br />
                  <strong>Branch:</strong> Wukari Branch
                </p>
              </div>
            </div>
          </div>
          <div className="alert alert-warning mt-3">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Important:</strong> Always include your manuscript ID and author name in the payment reference/description.
          </div>
        </>
      )
    },
    {
      title: 'Refund Policy',
      content: (
        <>
          <div className="alert alert-danger mb-3">
            <h5><i className="fas fa-ban me-2"></i>General Policy: No Refunds</h5>
            <p className="mb-0">
              Publication fees are generally non-refundable as they cover editorial services already provided. Refunds may be considered only in exceptional circumstances.
            </p>
          </div>

          <h5>Refund Exceptions</h5>
          <p>Refunds may be considered in the following cases:</p>
          <ul className="section-list">
            <li>Duplicate payments due to technical errors</li>
            <li>Overpayment due to currency conversion errors</li>
            <li>Journal decision to retract accepted article (not due to author misconduct)</li>
            <li>Force majeure circumstances preventing publication</li>
            <li>System errors resulting in incorrect charges</li>
          </ul>

          <p className="mt-3">
            <strong>To request a refund:</strong> Contact <a href="mailto:finance@fuwukari.edu.ng">finance@fuwukari.edu.ng</a> with your manuscript ID and payment details.
          </p>
        </>
      )
    },
    {
      title: 'Payment Confirmation',
      content: (
        <>
          <h5>Required Information</h5>
          <p>After completing payment, please send the following to <a href="mailto:payments@fuwukari.edu.ng">payments@fuwukari.edu.ng</a>:</p>
          <ul className="section-list">
            <li>Manuscript ID (provided in acceptance email)</li>
            <li>Payment method used</li>
            <li>Transaction reference number</li>
            <li>Payment date and amount</li>
            <li>Screenshot or PDF of payment confirmation</li>
            <li>Corresponding author name and email</li>
          </ul>

          <div className="bg-light p-3 rounded mt-3">
            <h6 className="text-primary">Email Subject Format:</h6>
            <code>Payment Confirmation - [Manuscript ID] - [Author Name]</code>
          </div>
        </>
      )
    },
    {
      title: 'Payment Support',
      content: 'Our dedicated support team is available to assist with payment-related queries:',
      highlight: (
        <>
          <div className="row">
            <div className="col-md-6">
              <strong>Technical Issues</strong><br />
              For payment processing problems:<br />
              <i className="fas fa-envelope me-2"></i>payments@fuwukari.edu.ng<br />
              <i className="fas fa-phone me-2"></i>+234 XXX XXX XXXX<br />
              <i className="fas fa-clock me-2"></i>Mon-Fri, 9 AM - 5 PM WAT
            </div>
            <div className="col-md-6">
              <strong>Financial Queries</strong><br />
              For fee and invoice questions:<br />
              <i className="fas fa-envelope me-2"></i>finance@fuwukari.edu.ng<br />
              <i className="fas fa-phone me-2"></i>+234 XXX XXX XXXX<br />
              <i className="fas fa-clock me-2"></i>Response within 24-48 hours
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <PolicyPageLayout
      title="Payment Methods"
      sections={sections}
      icon="💳"
    />
  );
};

export default PaymentMethods;